import { roundToUp } from "round-to"
import { formatNumber, formatNumberToMM } from "../../../utils/number"
import { isMobile } from "react-device-detect";
import { countOfOrdersToDate, getSalesTotalDate, getSalesTotalUntilDate, groupByDate, groupByHour, groupByImportType, groupByTrademark, groupByTypeOrder, groupByWeek, months, sumTotalCustomer, sumTotalValue, sumValueOfOrdersToDate, weeks } from "../../../utils/helper";

/**
 * 
 * @param {*} sales 
 * @param {*} lastSales 
 * @returns { caption, metric, badgeDelta, progress, target, deltaType }
 *  
 */
export const orderCountKPI = (sales) => {
    const currentMonthSales = sales?.currentMonthSales || [];
    const lastMonthSales = sales?.lastMonthSales || [];

    let result = {
        caption: 'Pedidos',
        metric: 0,
        badgeDelta: 0,
        progress: 0,
        target: 0,
        deltaType: 'moderateIncrease',
    };

    const lengthCurrentSales = currentMonthSales.length;

    // Retorna resultado inicial se não há vendas do mês atual
    if (lengthCurrentSales === 0) {
        return result;
    }

    result.metric = isMobile
        ? formatNumberToMM(lengthCurrentSales)
        : formatNumber(lengthCurrentSales, 'decimal');

    const lengthLastSales = lastMonthSales.length;

    if (lengthLastSales > 0) {
        result.target = isMobile
            ? formatNumberToMM(lengthLastSales)
            : formatNumber(lengthLastSales, 'decimal');

        result.progress = roundToUp((lengthCurrentSales / lengthLastSales) * 100, 2);
        result.badgeDelta = roundToUp(((lengthCurrentSales - lengthLastSales) / lengthLastSales) * 100, 2);

        // Calcula percentual considerando vendas do mês anterior até a data de "hoje"
        if (sales.isCurrentMonth) {
            const salesByToday = countOfOrdersToDate(lastMonthSales, sales.currentDay);
            if (salesByToday) {
                result.badgeDelta = roundToUp(((lengthCurrentSales - salesByToday) / salesByToday) * 100, 2);
                result.progress = roundToUp((lengthCurrentSales / salesByToday) * 100, 2);
            } else {
                result.badgeDelta = 0
                result.progress = 0
            }
        }
    }

    return result;
}

export const customerCountKPI = (sales) => {
    const currentMonthSales = sales?.currentMonthSales || [];
    const lastMonthSales = sales?.lastMonthSales || [];

    let result = {
        caption: 'Pessoas atendidas',
        metric: 0,
        badgeDelta: 0,
        progress: 0,
        target: 0,
        deltaType: 'moderateIncrease',
    };

    const lengthCurrentSales = currentMonthSales.length;

    // Retorna resultado inicial se não há vendas do mês atual
    if (lengthCurrentSales === 0) {
        return result;
    }

    const currentMonthCustomer = sumTotalCustomer(currentMonthSales)

    result.metric = isMobile
        ? formatNumberToMM(currentMonthCustomer)
        : formatNumber(currentMonthCustomer, 'decimal');

    const lengthLastSales = lastMonthSales.length;

    if (lengthLastSales > 0) {
        const lastMonthCustomer = sumTotalCustomer(lastMonthSales)

        result.target = isMobile
            ? formatNumberToMM(lastMonthCustomer)
            : formatNumber(lastMonthCustomer, 'decimal');

        result.progress = roundToUp((currentMonthCustomer / lastMonthCustomer) * 100, 2);
        result.badgeDelta = roundToUp(((currentMonthCustomer - lastMonthCustomer) / lastMonthCustomer) * 100, 2);

        // Calcula percentual considerando vendas do mês anterior até a data de "hoje"
        if (sales.isCurrentMonth) {
            const salesByToday = countOfOrdersToDate(lastMonthSales, sales.currentDay);
            if (salesByToday) {
                result.badgeDelta = roundToUp(((currentMonthCustomer - salesByToday) / salesByToday) * 100, 2);
                result.progress = roundToUp((currentMonthCustomer / salesByToday) * 100, 2);
            } else {
                result.badgeDelta = 0
                result.progress = 0
            }
        }
    }

    return result;
}

export const orderTotalKPI = (sales) => {
    const currentMonthSales = sales?.currentMonthSales || [];
    const lastMonthSales = sales?.lastMonthSales || [];

    let result = {
        caption: 'Valor total',
        metric: 0,
        badgeDelta: 0,
        progress: 0,
        target: 0,
        deltaType: 'moderateIncrease',
    };

    const currentMonthValue = sumTotalValue(currentMonthSales);

    // Retorna resultado inicial se não há vendas do mês atual
    if (currentMonthSales.length === 0) {
        return result;
    }

    result.metric = isMobile
        ? formatNumberToMM(currentMonthValue)
        : formatNumber(currentMonthValue, { style: 'currency' });

    if (lastMonthSales.length > 0) {
        const lastMonthValue = sumTotalValue(lastMonthSales);
        result.target = isMobile
            ? formatNumberToMM(lastMonthValue)
            : formatNumber(lastMonthValue, { style: 'currency' });

        result.progress = roundToUp((currentMonthValue / lastMonthValue) * 100, 2);
        result.badgeDelta = roundToUp(((currentMonthValue - lastMonthValue) / lastMonthValue) * 100, 2);

        // Calcula percentual considerando vendas do mês anterior até a data de "hoje"
        if (sales.isCurrentMonth) {
            const salesByToday = sumValueOfOrdersToDate(lastMonthSales, sales.currentDay);

            if (salesByToday > 0) {
                result.badgeDelta = roundToUp(((currentMonthValue - salesByToday) / salesByToday) * 100, 2);
                result.progress = roundToUp((currentMonthValue / salesByToday) * 100, 2);
            }
        }
    }

    return result;
}

export const orderAverageTicketKPI = (sales) => {
    const currentMonthSales = sales?.currentMonthSales || [];
    const lastMonthSales = sales?.lastMonthSales || [];

    let result = {
        caption: 'Ticket médio - Pedido',
        metric: 0,
        badgeDelta: 0,
        progress: 0,
        target: 0,
        deltaType: 'moderateIncrease',
    };

    // Retorna resultado inicial se não há vendas do mês atual
    if (currentMonthSales.length === 0) {
        return result;
    }

    const currentMonthLength = currentMonthSales.length;
    const currentMonthValue = sumTotalValue(currentMonthSales);
    const currentMetric = currentMonthValue / currentMonthLength;

    result.metric = formatNumber(roundToUp(currentMetric, 2), { style: 'currency' });

    if (lastMonthSales.length > 0) {
        const lastMonthLength = lastMonthSales.length;
        const lastMonthValue = sumTotalValue(lastMonthSales);
        const currentTarget = lastMonthValue / lastMonthLength;

        result.target = formatNumber(roundToUp(currentTarget, 2), { style: 'currency' });
        result.badgeDelta = roundToUp(((currentMetric - currentTarget) / currentTarget) * 100, 2);
        result.progress = roundToUp((currentMetric / currentTarget) * 100, 2);
    }

    // Calcula percentual considerando vendas do mês anterior até a data de "hoje"
    if (sales.isCurrentMonth) {
        const salesByTodayValue = sumValueOfOrdersToDate(lastMonthSales, sales.currentDay);

        if (salesByTodayValue > 0) {
            const salesByTodayLength = countOfOrdersToDate(lastMonthSales, sales.currentDay);
            const salesByTodayTarget = salesByTodayValue / salesByTodayLength;

            result.badgeDelta = roundToUp(((currentMetric - salesByTodayTarget) / salesByTodayTarget) * 100, 2);
            result.progress = roundToUp((currentMetric / salesByTodayTarget) * 100, 2);
        }
    }

    return result;
}

export const orderAverageTicketCustomerKPI = (sales) => {
    const currentMonthSales = sales?.currentMonthSales || [];
    const lastMonthSales = sales?.lastMonthSales || [];

    let result = {
        caption: 'Ticket médio - Pessoa',
        metric: 0,
        badgeDelta: 0,
        progress: 0,
        target: 0,
        deltaType: 'moderateIncrease',
    };

    // Retorna resultado inicial se não há vendas do mês atual
    if (currentMonthSales.length === 0) {
        return result;
    }

    const currentMonthCustomet = sumTotalCustomer(currentMonthSales)
    // const currentMonthLength = currentMonthSales.length;
    const currentMonthValue = sumTotalValue(currentMonthSales);
    const currentMetric = currentMonthValue / currentMonthCustomet;

    result.metric = formatNumber(roundToUp(currentMetric, 2), { style: 'currency' });

    if (lastMonthSales.length > 0) {
        const lastMonthCustomet = sumTotalCustomer(lastMonthSales)
        // const lastMonthLength = lastMonthSales.length;
        const lastMonthValue = sumTotalValue(lastMonthSales);
        const currentTarget = lastMonthValue / lastMonthCustomet;

        result.target = formatNumber(roundToUp(currentTarget, 2), { style: 'currency' });
        result.badgeDelta = roundToUp(((currentMetric - currentTarget) / currentTarget) * 100, 2);
        result.progress = roundToUp((currentMetric / currentTarget) * 100, 2);
    }

    // Calcula percentual considerando vendas do mês anterior até a data de "hoje"
    if (sales.isCurrentMonth) {
        const salesByTodayValue = sumValueOfOrdersToDate(lastMonthSales, sales.currentDay);

        if (salesByTodayValue > 0) {
            const salesByTodayLength = countOfOrdersToDate(lastMonthSales, sales.currentDay);
            const salesByTodayTarget = salesByTodayValue / salesByTodayLength;

            result.badgeDelta = roundToUp(((currentMetric - salesByTodayTarget) / salesByTodayTarget) * 100, 2);
            result.progress = roundToUp((currentMetric / salesByTodayTarget) * 100, 2);
        }
    }

    return result;
}

export const cumulativeSalesPerDateKPI = (sales, metaTotal = 0) => {
    const currentMonthSales = sales?.currentMonthSales || [];

    let result = {
        caption: 'Vendas acumuladas por dia',
        data: [],
        index: "date",
        categories: [],
        colors: ["indigo", "cyan", "emerald", "amber"],
    };

    if (currentMonthSales.length === 0) {
        return result;
    }

    const actualMonth = sales.currentMonth - 1;
    const lastMonth = (actualMonth === 0) ? 11 : actualMonth - 1;

    const currentMonthDescription = months[actualMonth];
    const lastMonthDescription = months[lastMonth];
    const currentMonthSalesByDay = `Vendas diárias de ${currentMonthDescription}`;
    const metaDescription = "Meta Acumulada"

    result.categories.push(lastMonthDescription, currentMonthDescription, currentMonthSalesByDay, metaDescription)

    const groupedDataCurrentMonth = groupByDate(currentMonthSales);
    const groupedDataLastMonth = groupByDate(sales.lastMonthSales);

    const currentYear = new Date().getFullYear()
    const daysInCurrentMonth = new Date(currentYear, sales.currentMonth, 0).getDate()

    const chartData = Array.from({ length: 31 }, (_, day) => {
        const dayIndex = day + 1

        let metaAcumuladaAteHoje = 0

        if (dayIndex <= daysInCurrentMonth) {
            metaAcumuladaAteHoje = (metaTotal / daysInCurrentMonth) * dayIndex
        } else {
            metaAcumuladaAteHoje = metaTotal
        }

        return {
            date: dayIndex,
            [lastMonthDescription]: getSalesTotalUntilDate(groupedDataLastMonth, dayIndex),
            [currentMonthDescription]: getSalesTotalUntilDate(groupedDataCurrentMonth, dayIndex),
            [currentMonthSalesByDay]: getSalesTotalDate(groupedDataCurrentMonth, dayIndex),
            [metaDescription]: Number(metaAcumuladaAteHoje.toFixed(2)),
        };
    });

    result.data = chartData;

    return result;
}


export const cumulativeSalesPerWeekKPI = (sales) => {
    const existsCurrentMonthSales = sales && sales.currentMonthSales && sales.currentMonthSales.length;

    let result = {
        caption: 'Vendas por dia da semana',
        data: [],
        index: "week",
        categories: [],
        colors: ["indigo", "cyan"],
    }

    if (!existsCurrentMonthSales) {
        return result;
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1;

    if (lastMonth < 0) {
        lastMonth = 11;
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    // const lastMonthDescription = `${months[lastMonth]}`;

    result.categories.push(currentMonthDescription);
    // result.categories.push(lastMonthDescription, currentMonthDescription);

    const groupedDataCurrentMonth = groupByWeek(sales.currentMonthSales);

    // let groupedDataLastMonth

    //Filtra os registros até a data atual caso seja o mês atual
    // if ((!sales.compareWith || sales.compareWith === 'CURRENT_DATE') && sales.isCurrentMonth) {
    //     groupedDataLastMonth = groupByWeek(filterRecordsByDate(sales.lastMonthSales, sales.currentDay)); 
    // } else {
    //     groupedDataLastMonth = groupByWeek(sales.lastMonthSales)
    // }

    function filterByWeek(data, week) {
        return data?.find(item => item?.week === week);
    }

    let chartData = [];

    for (let week = 0; week <= 6; week++) {
        chartData.push({
            week: weeks[week],
            // [lastMonthDescription]: sumTotalValue(filterByWeek(groupedDataLastMonth, week)?.records),
            [currentMonthDescription]: sumTotalValue(filterByWeek(groupedDataCurrentMonth, week)?.records),
        });
    }

    // 1) total do mês atual
    const totalCurrentMonth = chartData.reduce(
        (acc, item) => acc + (item[currentMonthDescription] || 0),
        0
    );

    // 2) adiciona percentual (0–100 com 2 casas)
    const withPercent = chartData.map(item => ({
        ...item,
        percentage: totalCurrentMonth > 0
            ? Number(((item[currentMonthDescription] / totalCurrentMonth) * 100).toFixed(2))
            : 0
    }));

    // 3) ordena em ordem decrescente pelo valor do mês atual
    // withPercent.sort((a, b) => (b[currentMonthDescription] || 0) - (a[currentMonthDescription] || 0));

    result.data = withPercent;

    return result;
}


export const cumulativeSalesPerTypeOrderKPI = (sales) => {

    let result = {
        caption: 'Vendas por tipo de pedido',
        data: [],
        index: "typeOrder",
        categories: [],
        colors: ["indigo", "cyan"],
        // layout: 'vertical',
    }

    const existsCurrentMonthSales = sales && sales.currentMonthSales && sales.currentMonthSales.length

    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1

    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    // const lastMonthDescription = `${months[lastMonth]}`;

    result.categories.push(currentMonthDescription)
    // result.categories.push(lastMonthDescription, currentMonthDescription)

    const groupedDataCurrentMonth = groupByTypeOrder(sales.currentMonthSales)

    // let groupedDataLastMonth

    //Filtra os registros até a data atual caso seja o mês atual
    // if ((!sales.compareWith || sales.compareWith === 'CURRENT_DATE') && sales.isCurrentMonth) {
    //     groupedDataLastMonth = groupByTypeOrder(filterRecordsByDate(sales.lastMonthSales, sales.currentDay));
    // } else {
    //     groupedDataLastMonth = groupByTypeOrder(sales.lastMonthSales)
    // }

    function filterByTypeOrder(data, typeOrder) {
        return data?.find(item => item?.typeOrder?.toUpperCase() === typeOrder?.toUpperCase());
    }

    function extractTypeOrders(data) {
        const typeOrders = data.map(item => item.typeOrder || 'Comanda');
        return typeOrders.filter((value, index) => typeOrders.indexOf(value?.toUpperCase()) === index);
    }

    const groupedData = [...groupedDataCurrentMonth];
    // const groupedData = [...groupedDataCurrentMonth, ...groupedDataLastMonth];

    const orderTypeList = extractTypeOrders(groupedData)

    let chartData = [];

    orderTypeList.forEach(type => {
        chartData.push({
            typeOrder: type,
            // [lastMonthDescription]: sumTotalValue(filterByTypeOrder(groupedDataLastMonth, type)?.records),
            [currentMonthDescription]: sumTotalValue(filterByTypeOrder(groupedDataCurrentMonth, type)?.records),
        });
    })

    // 1) total do mês atual
    const totalCurrentMonth = chartData.reduce(
        (acc, item) => acc + (item[currentMonthDescription] || 0),
        0
    );

    // 2) adiciona percentual (0–100 com 2 casas)
    const withPercent = chartData.map(item => ({
        ...item,
        percentage: totalCurrentMonth > 0
            ? Number(((item[currentMonthDescription] / totalCurrentMonth) * 100).toFixed(2))
            : 0
    }));

    // 3) ordena em ordem decrescente pelo valor do mês atual
    withPercent.sort((a, b) => (b[currentMonthDescription] || 0) - (a[currentMonthDescription] || 0));



    result.data = withPercent

    return result
}

export const salesPerHourKPI = (sales) => {
    let result = {
        caption: 'Vendas por hora',
        data: [],
        index: "hour",
        categories: [],
        colors: ["indigo", "cyan"],
    }

    const existsCurrentMonthSales = sales && sales.currentMonthSales && sales.currentMonthSales.length

    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1

    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    // const lastMonthDescription = `${months[lastMonth]}`;

    result.categories.push(currentMonthDescription)
    // result.categories.push(lastMonthDescription, currentMonthDescription)

    const groupedDataCurrentMonth = groupByHour(sales.currentMonthSales)

    // let groupedDataLastMonth

    //Filtra os registros até a data atual caso seja o mês atual
    // if ((!sales.compareWith || sales.compareWith === 'CURRENT_DATE') && sales.isCurrentMonth) {
    //     groupedDataLastMonth = groupByHour(filterRecordsByDate(sales.lastMonthSales, sales.currentDay));
    // } else {
    //     groupedDataLastMonth = groupByHour(sales.lastMonthSales)
    // }

    function filterByHour(data, hour) {
        return data?.find(item => item?.hour === hour);
    }

    let chartData = [];

    for (let hour = 0; hour <= 23; hour++) {
        chartData.push({
            hour,
            // [lastMonthDescription]: sumTotalValue(filterByHour(groupedDataLastMonth, hour)?.records),
            [currentMonthDescription]: sumTotalValue(filterByHour(groupedDataCurrentMonth, hour)?.records),
        });
    }

    // 1) total do mês atual
    const totalCurrentMonth = chartData.reduce(
        (acc, item) => acc + (item[currentMonthDescription] || 0),
        0
    );

    // 2) adiciona percentual (0–100 com 2 casas)
    const withPercent = chartData.map(item => ({
        ...item,
        percentage: totalCurrentMonth > 0
            ? Number(((item[currentMonthDescription] / totalCurrentMonth) * 100).toFixed(2))
            : 0
    }));

    result.data = withPercent

    return result
}

export const salesPerImportTypeKPI = (sales) => {
    let result = {
        caption: 'Vendas por canal',
        data: [],
        layout: 'vertical',
        index: "typeOrder",
        categories: [],
        colors: ["indigo", "cyan"],
        yAxisWidth: 90,
    }

    const existsCurrentMonthSales = sales && sales.currentMonthSales && sales.currentMonthSales.length
    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1
    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    result.categories.push(currentMonthDescription)

    const groupedDataCurrentMonth = groupByImportType(sales.currentMonthSales)

    function filterByImportType(data, importType) {
        return data?.find(item => item?.importType?.toUpperCase() === importType?.toUpperCase());
    }

    function extractImportType(data) {
        const importTypes = data.map(item => item.importType?.toUpperCase() || 'Comanda');
        return importTypes.filter((value, index) => importTypes.indexOf(value?.toUpperCase()) === index);
    }

    const groupedData = [...groupedDataCurrentMonth];
    const importTypeList = extractImportType(groupedData)

    let chartData = [];
    importTypeList.forEach(type => {
        chartData.push({
            typeOrder: type,
            [currentMonthDescription]: sumTotalValue(filterByImportType(groupedDataCurrentMonth, type)?.records),
        });
    });

    // 1) total do mês atual
    const totalCurrentMonth = chartData.reduce(
        (acc, item) => acc + (item[currentMonthDescription] || 0),
        0
    );

    // 2) adiciona percentual (0–100 com 2 casas)
    const withPercent = chartData.map(item => ({
        ...item,
        percentage: totalCurrentMonth > 0
            ? Number(((item[currentMonthDescription] / totalCurrentMonth) * 100).toFixed(2))
            : 0
    }));

    // 3) ordena em ordem decrescente pelo valor do mês atual
    withPercent.sort((a, b) => (b[currentMonthDescription] || 0) - (a[currentMonthDescription] || 0));

    result.data = withPercent;
    return result;
}

export const salesPerTrademarkKPI = (sales) => {
    let result = {
        caption: 'Vendas por marca',
        data: [],
        layout: 'vertical',
        index: "typeOrder",
        categories: [],
        colors: ["indigo", "cyan"],
        yAxisWidth: 90,
    }

    // console.log('sales', sales)

    const existsCurrentMonthSales = sales && sales.currentMonthSales && sales.currentMonthSales.length
    if (!existsCurrentMonthSales) {
        return result
    }

    const actualMonth = sales.currentMonth - 1;
    let lastMonth = actualMonth - 1
    if (lastMonth < 0) {
        lastMonth = 11
    }

    const currentMonthDescription = `${months[actualMonth]}`;
    result.categories.push(currentMonthDescription)

    const groupedDataCurrentMonth = groupByTrademark(sales.currentMonthSales)

    function filterByImportType(data, trademark) {
        return data?.find(item => item?.trademark?.toUpperCase() === trademark?.toUpperCase())
    }

    function extractImportType(data) {
        const trademarks = data.map(item => item.trademark?.toUpperCase() || 'Outros')
        return trademarks.filter((value, index) => trademarks.indexOf(value?.toUpperCase()) === index)
    }

    const groupedData = [...groupedDataCurrentMonth];
    const importTypeList = extractImportType(groupedData)

    let chartData = [];
    importTypeList.forEach(type => {
        chartData.push({
            typeOrder: type,
            [currentMonthDescription]: sumTotalValue(filterByImportType(groupedDataCurrentMonth, type)?.records),
        });
    });

    // 1) total do mês atual
    const totalCurrentMonth = chartData.reduce(
        (acc, item) => acc + (item[currentMonthDescription] || 0),
        0
    );

    // 2) adiciona percentual (0–100 com 2 casas)
    const withPercent = chartData.map(item => ({
        ...item,
        percentage: totalCurrentMonth > 0
            ? Number(((item[currentMonthDescription] / totalCurrentMonth) * 100).toFixed(2))
            : 0
    }));

    // 3) ordena em ordem decrescente pelo valor do mês atual
    withPercent.sort((a, b) => (b[currentMonthDescription] || 0) - (a[currentMonthDescription] || 0));

    result.data = withPercent;
    return result;
}